import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import {
  TenantsAPI, InstitutionsAPI, YearsAPI, ClassesAPI, SectionsAPI, SubjectsAPI,
} from "@/lib/sprint2-api";
import { PeriodsAPI } from "@/lib/academic-extras-api";
import { ExamsAPI } from "@/lib/gradebook-api";
import { TimetableAPI, ExamScheduleAPI } from "@/lib/timetable-api";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Clock3, User as UserIcon, CalendarRange, X, Save } from "lucide-react";

const WEEKDAYS = [
  { idx: 0, label: "Mon" },
  { idx: 1, label: "Tue" },
  { idx: 2, label: "Wed" },
  { idx: 3, label: "Thu" },
  { idx: 4, label: "Fri" },
  { idx: 5, label: "Sat" },
];

export default function Timetable() {
  const { user } = useAuth();
  const isSuper = user?.roles?.includes("super_admin");

  const [tenantId, setTenantId] = useState("");
  const [institutionId, setInstitutionId] = useState("");
  const [yearId, setYearId] = useState("");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");

  const tenantsQuery = useQuery({
    queryKey: ["tenants"],
    queryFn: () => TenantsAPI.list(),
    enabled: isSuper,
  });
  const effectiveTenantId = isSuper
    ? (tenantId || tenantsQuery.data?.[0]?.id || "")
    : (user?.tenant_id || "");
  const tenantParam = isSuper && effectiveTenantId ? { tenant_id: effectiveTenantId } : undefined;

  const institutionsQuery = useQuery({
    queryKey: ["institutions", effectiveTenantId],
    queryFn: () => InstitutionsAPI.list(tenantParam),
    enabled: !!effectiveTenantId,
  });
  const effectiveInstitutionId =
    (institutionsQuery.data?.some((i) => i.id === institutionId) ? institutionId : "") ||
    institutionsQuery.data?.[0]?.id ||
    "";

  const yearsQuery = useQuery({
    queryKey: ["years", effectiveInstitutionId],
    queryFn: () => YearsAPI.list({ institution_id: effectiveInstitutionId, ...(tenantParam || {}) }),
    enabled: !!effectiveInstitutionId,
  });
  const effectiveYearId = (() => {
    const list = yearsQuery.data || [];
    if (list.some((y) => y.id === yearId)) return yearId;
    return list.find((y) => y.is_current)?.id || list[0]?.id || "";
  })();

  const classesQuery = useQuery({
    queryKey: ["classes", effectiveYearId],
    queryFn: () => ClassesAPI.list({ academic_year_id: effectiveYearId, ...(tenantParam || {}) }),
    enabled: !!effectiveYearId,
  });
  const effectiveClassId =
    (classesQuery.data?.some((c) => c.id === classId) ? classId : "") ||
    classesQuery.data?.[0]?.id ||
    "";

  const sectionsQuery = useQuery({
    queryKey: ["sections", effectiveClassId],
    queryFn: () => SectionsAPI.list({ class_id: effectiveClassId, ...(tenantParam || {}) }),
    enabled: !!effectiveClassId,
  });

  return (
    <AppShell>
      <div className="p-8" data-testid="timetable-page">
        <div className="mb-6">
          <h1 className="font-display font-extrabold tracking-tight text-4xl">Timetable &amp; Exams</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Compose weekly schedules: each cell binds a <span className="font-mono text-xs px-1 bg-secondary rounded">period</span> on a weekday to a subject + teacher. Conflicts (same teacher in two places at once) are hard-blocked. The Exam Schedule tab gives you an inline-editable calendar of all Gradebook exams in scope.
          </p>
        </div>

        <div className="flex flex-wrap gap-3 mb-6 p-4 border border-border rounded-md bg-card">
          {isSuper && (
            <Scope label="Tenant" value={effectiveTenantId} onChange={(v) => { setTenantId(v); setInstitutionId(""); setYearId(""); setClassId(""); setSectionId(""); }} options={tenantsQuery.data || []} testid="tt-scope-tenant" />
          )}
          <Scope label="Institution" value={effectiveInstitutionId} onChange={(v) => { setInstitutionId(v); setYearId(""); setClassId(""); setSectionId(""); }} options={institutionsQuery.data || []} testid="tt-scope-institution" />
          <Scope label="Academic year" value={effectiveYearId} onChange={(v) => { setYearId(v); setClassId(""); setSectionId(""); }} options={(yearsQuery.data || []).map((y) => ({ id: y.id, name: y.name + (y.is_current ? " · current" : "") }))} disabled={!effectiveInstitutionId} testid="tt-scope-year" />
          <Scope label="Class" value={effectiveClassId} onChange={(v) => { setClassId(v); setSectionId(""); }} options={classesQuery.data || []} disabled={!effectiveYearId} testid="tt-scope-class" />
          <Scope label="Section" value={sectionId || "all"} onChange={(v) => setSectionId(v === "all" ? "" : v)} options={[{ id: "all", name: "All sections" }, ...(sectionsQuery.data || [])]} disabled={!effectiveClassId} testid="tt-scope-section" />
        </div>

        <Tabs defaultValue="grid">
          <TabsList>
            <TabsTrigger value="grid" data-testid="tt-tab-grid"><Clock3 className="h-3.5 w-3.5 mr-1.5" />Weekly Grid</TabsTrigger>
            <TabsTrigger value="teacher" data-testid="tt-tab-teacher"><UserIcon className="h-3.5 w-3.5 mr-1.5" />Teacher view</TabsTrigger>
            <TabsTrigger value="exams" data-testid="tt-tab-exams"><CalendarRange className="h-3.5 w-3.5 mr-1.5" />Exam Schedule</TabsTrigger>
          </TabsList>

          <TabsContent value="grid" className="mt-6">
            <ClassGrid
              tenantId={effectiveTenantId}
              institutionId={effectiveInstitutionId}
              classId={effectiveClassId}
              sectionId={sectionId}
            />
          </TabsContent>
          <TabsContent value="teacher" className="mt-6">
            <TeacherGrid
              tenantId={effectiveTenantId}
              institutionId={effectiveInstitutionId}
            />
          </TabsContent>
          <TabsContent value="exams" className="mt-6">
            <ExamSchedule
              tenantId={effectiveTenantId}
              classId={effectiveClassId}
            />
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}

function Scope({ label, value, onChange, options, disabled, testid }) {
  return (
    <div className="space-y-1.5 min-w-[160px]">
      <Label className="text-[10px] font-semibold uppercase tracking-wider">{label}</Label>
      <Select value={value} onValueChange={onChange} disabled={disabled || !options.length}>
        <SelectTrigger className="w-full" data-testid={testid}>
          <SelectValue placeholder={disabled ? "—" : `Select ${label.toLowerCase()}`} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

// ---------------- Class weekly grid ----------------
function ClassGrid({ tenantId, institutionId, classId, sectionId }) {
  const queryClient = useQueryClient();
  const periodsQuery = useQuery({
    queryKey: ["tt-periods", tenantId || "self", institutionId],
    queryFn: () => PeriodsAPI.list(institutionId, tenantId),
    enabled: !!institutionId,
  });
  const slotsQuery = useQuery({
    queryKey: ["tt-slots-class", tenantId || "self", classId, sectionId || "any"],
    queryFn: () => TimetableAPI.listByClass({ classId, sectionId: sectionId || null, institutionId }, tenantId),
    enabled: !!classId,
  });
  const subjectsQuery = useQuery({
    queryKey: ["tt-subjects", classId],
    queryFn: () => SubjectsAPI.list({ class_id: classId, ...(tenantId ? { tenant_id: tenantId } : {}) }),
    enabled: !!classId,
  });
  // Fetch the pool of teachers so cells can render the teacher's name.
  const teachersQuery = useQuery({
    queryKey: ["tt-teachers", tenantId || "self", institutionId],
    queryFn: () => TimetableAPI.teachers(institutionId, tenantId),
    enabled: !!institutionId,
  });
  const teachersById = new Map((teachersQuery.data || []).map((t) => [t.id, t]));

  const [editing, setEditing] = useState(null); // {periodId, weekday, existing}

  if (!classId) return <Empty msg="Pick a class to compose its weekly schedule." />;
  if (periodsQuery.isLoading || slotsQuery.isLoading) return <Loading />;
  if (!(periodsQuery.data || []).length) {
    return <Empty msg="Define periods first under Academic Setup → Calendar & Rules → Periods." />;
  }

  const slotsByKey = new Map();
  // When a specific section is picked we key by (period, day, section). When
  // "All sections" is selected we key by (period, day) so the first slot for
  // that cell is displayed — otherwise classes with a single section render
  // as an empty grid.
  (slotsQuery.data || []).forEach((s) => {
    slotsByKey.set(keyOf(s), s);
    if (!sectionId) slotsByKey.set(`${s.period_id}-${s.weekday}-`, s);
  });
  const subjectsById = new Map((subjectsQuery.data || []).map((s) => [s.id, s]));

  const removeSlot = async (id) => {
    if (!window.confirm("Clear this cell?")) return;
    try { await TimetableAPI.remove(id, tenantId); queryClient.invalidateQueries({ queryKey: ["tt-slots-class"] }); toast.success("Cleared"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <>
      <div className="border border-border rounded-md bg-card overflow-x-auto" data-testid="tt-class-grid">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/40">
              <th className="text-left px-3 py-2 font-medium text-xs uppercase tracking-wider w-44">Period</th>
              {WEEKDAYS.map((d) => (
                <th key={d.idx} className="text-left px-3 py-2 font-medium text-xs uppercase tracking-wider min-w-[160px]">{d.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(periodsQuery.data || []).map((p) => (
              <tr key={p.id} className="border-t border-border">
                <td className="px-3 py-2 align-top">
                  <div className="font-medium">{p.name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{p.start_time}–{p.end_time}</div>
                </td>
                {WEEKDAYS.map((d) => {
                  const slot = slotsByKey.get(`${p.id}-${d.idx}-${sectionId || ""}`);
                  const sub = slot ? subjectsById.get(slot.subject_id) : null;
                  return (
                    <td key={d.idx} className="px-2 py-2 align-top" data-testid={`tt-cell-${p.id}-${d.idx}`}>
                      {slot ? (
                        <div className="border border-border rounded-md px-2 py-1.5 bg-primary/5 group relative">
                          <div className="font-medium leading-tight">{sub?.name || "Subject?"}</div>
                          <div className="text-[10px] text-muted-foreground mt-0.5 truncate">
                            {teachersById.get(slot.teacher_id)?.name || `Teacher #${slot.teacher_id.slice(-5)}`}
                          </div>
                          <div className="flex gap-1 absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition">
                            <button type="button" onClick={() => setEditing({ periodId: p.id, weekday: d.idx, existing: slot })} className="bg-card border border-border rounded-full h-5 w-5 flex items-center justify-center hover:bg-secondary" title="Edit" data-testid={`tt-cell-edit-${p.id}-${d.idx}`}>
                              <span className="text-[10px]">✎</span>
                            </button>
                            <button type="button" onClick={() => removeSlot(slot.id)} className="bg-card border border-destructive/30 rounded-full h-5 w-5 flex items-center justify-center text-destructive hover:bg-destructive/10" title="Clear" data-testid={`tt-cell-clear-${p.id}-${d.idx}`}>
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setEditing({ periodId: p.id, weekday: d.idx, existing: null })}
                          className="w-full border border-dashed border-border rounded-md px-2 py-2 text-xs text-muted-foreground hover:bg-secondary hover:border-primary/40 transition"
                          data-testid={`tt-cell-add-${p.id}-${d.idx}`}
                        >
                          <Plus className="h-3 w-3 inline mr-1" /> assign
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <SlotDialog
          open={!!editing}
          onClose={() => setEditing(null)}
          tenantId={tenantId}
          institutionId={institutionId}
          classId={classId}
          sectionId={sectionId}
          periodId={editing.periodId}
          weekday={editing.weekday}
          existing={editing.existing}
          subjects={subjectsQuery.data || []}
          onSaved={() => { queryClient.invalidateQueries({ queryKey: ["tt-slots-class"] }); setEditing(null); }}
        />
      )}
    </>
  );
}

function keyOf(s) {
  return `${s.period_id}-${s.weekday}-${s.section_id || ""}`;
}

function SlotDialog({ open, onClose, tenantId, institutionId, classId, sectionId, periodId, weekday, existing, subjects, onSaved }) {
  const [subjectId, setSubjectId] = useState(existing?.subject_id || "");
  const [teacherId, setTeacherId] = useState(existing?.teacher_id || "");
  const [busy, setBusy] = useState(false);

  // Teacher list = subject.teacher_ids of the picked subject; resolved client-side.
  const subject = subjects.find((s) => s.id === subjectId);
  const teacherIds = subject?.teacher_ids || [];

  const teachersQuery = useQuery({
    queryKey: ["tt-teachers", tenantId || "self", institutionId],
    queryFn: () => TimetableAPI.teachers(institutionId, tenantId),
    enabled: !!institutionId,
  });
  const teacherById = useMemo(
    () => new Map((teachersQuery.data || []).map((t) => [t.id, t])),
    [teachersQuery.data]
  );
  const allowedTeachers = teacherIds.map((tid) => teacherById.get(tid)).filter(Boolean);

  const save = async () => {
    if (!subjectId || !teacherId) return toast.error("Pick a subject and a teacher");
    setBusy(true);
    try {
      await TimetableAPI.upsert({
        institution_id: institutionId,
        class_id: classId,
        section_id: sectionId || null,
        period_id: periodId,
        weekday,
        subject_id: subjectId,
        teacher_id: teacherId,
      }, tenantId);
      toast.success("Slot saved");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="tt-slot-dialog">
        <DialogHeader>
          <DialogTitle>{existing ? "Edit slot" : "Assign slot"} · {WEEKDAYS[weekday]?.label}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Subject">
            <Select value={subjectId} onValueChange={(v) => { setSubjectId(v); setTeacherId(""); }}>
              <SelectTrigger data-testid="tt-slot-subject"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label={`Teacher (from subject's assigned teachers · ${allowedTeachers.length})`}>
            <Select value={teacherId} onValueChange={setTeacherId} disabled={!subjectId || !allowedTeachers.length}>
              <SelectTrigger data-testid="tt-slot-teacher">
                <SelectValue placeholder={!subjectId ? "Pick a subject first" : (allowedTeachers.length ? "Select teacher" : "No teachers on this subject")} />
              </SelectTrigger>
              <SelectContent>
                {allowedTeachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {subjectId && !allowedTeachers.length && (
              <div className="text-[10px] text-muted-foreground mt-1">Assign teachers to this subject under Academic Setup → Subjects to unlock this dropdown.</div>
            )}
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !subjectId || !teacherId} data-testid="tt-slot-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            <Save className="h-3.5 w-3.5 mr-1.5" />
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Teacher pivot ----------------
function TeacherGrid({ tenantId, institutionId }) {
  const [teacherId, setTeacherId] = useState("");
  const teachersQuery = useQuery({
    queryKey: ["tt-teachers", tenantId || "self", institutionId],
    queryFn: () => TimetableAPI.teachers(institutionId, tenantId),
    enabled: !!institutionId,
  });
  const periodsQuery = useQuery({
    queryKey: ["tt-periods", tenantId || "self", institutionId],
    queryFn: () => PeriodsAPI.list(institutionId, tenantId),
    enabled: !!institutionId,
  });
  const slotsQuery = useQuery({
    queryKey: ["tt-slots-teacher", tenantId || "self", teacherId],
    queryFn: () => TimetableAPI.listByTeacher({ teacherId, institutionId }, tenantId),
    enabled: !!teacherId,
  });

  if (!institutionId) return <Empty msg="Pick an institution to view teacher schedules." />;

  const slotsByKey = new Map();
  (slotsQuery.data || []).forEach((s) => slotsByKey.set(`${s.period_id}-${s.weekday}`, s));

  return (
    <div className="space-y-3">
      <div className="border border-border rounded-md bg-card p-4 flex gap-3 items-end">
        <div className="min-w-[280px]">
          <Label className="text-[10px] font-semibold uppercase tracking-wider">Teacher</Label>
          <Select value={teacherId} onValueChange={setTeacherId} disabled={!teachersQuery.data?.length}>
            <SelectTrigger data-testid="tt-teacher-select">
              <SelectValue placeholder={teachersQuery.data?.length ? "Pick a teacher" : "No teachers in this institution"} />
            </SelectTrigger>
            <SelectContent>
              {(teachersQuery.data || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {teacherId && (periodsQuery.isLoading || slotsQuery.isLoading ? <Loading /> : (
        <div className="border border-border rounded-md bg-card overflow-x-auto" data-testid="tt-teacher-grid">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/40">
                <th className="text-left px-3 py-2 font-medium text-xs uppercase tracking-wider w-44">Period</th>
                {WEEKDAYS.map((d) => <th key={d.idx} className="text-left px-3 py-2 font-medium text-xs uppercase tracking-wider min-w-[140px]">{d.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {(periodsQuery.data || []).map((p) => (
                <tr key={p.id} className="border-t border-border">
                  <td className="px-3 py-2 align-top">
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{p.start_time}–{p.end_time}</div>
                  </td>
                  {WEEKDAYS.map((d) => {
                    const slot = slotsByKey.get(`${p.id}-${d.idx}`);
                    return (
                      <td key={d.idx} className="px-2 py-2 align-top">
                        {slot ? (
                          <div className="border border-border rounded-md px-2 py-1.5 bg-secondary text-xs">
                            <div className="font-medium">{slot.class_id?.slice(-4)}</div>
                            <div className="text-[10px] text-muted-foreground">subject · {slot.subject_id?.slice(-4)}</div>
                          </div>
                        ) : <div className="text-[10px] text-muted-foreground/60 px-2 py-1.5">—</div>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

// ---------------- Exam Schedule (inline-edit) ----------------
function ExamSchedule({ tenantId, classId }) {
  const queryClient = useQueryClient();
  const examsQuery = useQuery({
    queryKey: ["tt-exams", tenantId || "self", classId],
    queryFn: () => ExamsAPI.list({ class_id: classId }, tenantId),
    enabled: !!classId,
  });

  if (!classId) return <Empty msg="Pick a class to view its exam schedule." />;
  if (examsQuery.isLoading) return <Loading />;
  const exams = (examsQuery.data || []);
  if (!exams.length) return <Empty msg="No exams scheduled for this class yet. Create them in the Gradebook." />;

  // Group by date
  const groups = exams.reduce((acc, e) => {
    (acc[e.exam_date] = acc[e.exam_date] || []).push(e);
    return acc;
  }, {});
  const sortedDates = Object.keys(groups).sort();

  const patchExam = async (eid, patch) => {
    try {
      await ExamScheduleAPI.patch(eid, patch, tenantId);
      queryClient.invalidateQueries({ queryKey: ["tt-exams"] });
      toast.success("Updated");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="tt-exam-schedule">
      {sortedDates.map((date) => (
        <div key={date} className="border border-border rounded-md bg-card">
          <div className="px-4 py-2 border-b border-border bg-muted/30 flex items-center justify-between">
            <div className="font-mono text-sm">{date}</div>
            <Badge variant="outline" className="text-[10px]">{groups[date].length} exam{groups[date].length === 1 ? "" : "s"}</Badge>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead className="w-40">Date</TableHead>
                <TableHead className="w-32">Max marks</TableHead>
                <TableHead className="w-28">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups[date].map((e) => <ExamRow key={e.id} exam={e} onPatch={(p) => patchExam(e.id, p)} />)}
            </TableBody>
          </Table>
        </div>
      ))}
    </div>
  );
}

function ExamRow({ exam, onPatch }) {
  const [draft, setDraft] = useState({ title: exam.title, exam_date: exam.exam_date, max_marks: exam.max_marks });
  const dirty = draft.title !== exam.title || draft.exam_date !== exam.exam_date || Number(draft.max_marks) !== exam.max_marks;
  const save = () => {
    const patch = {};
    if (draft.title !== exam.title) patch.title = draft.title;
    if (draft.exam_date !== exam.exam_date) patch.exam_date = draft.exam_date;
    if (Number(draft.max_marks) !== exam.max_marks) patch.max_marks = Number(draft.max_marks);
    if (Object.keys(patch).length) onPatch(patch);
  };
  return (
    <TableRow data-testid={`tt-exam-row-${exam.id}`}>
      <TableCell>
        <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="h-8" data-testid={`tt-exam-title-${exam.id}`} />
      </TableCell>
      <TableCell>
        <Input type="date" value={draft.exam_date} onChange={(e) => setDraft({ ...draft, exam_date: e.target.value })} className="h-8 font-mono" data-testid={`tt-exam-date-${exam.id}`} />
      </TableCell>
      <TableCell>
        <Input type="number" min="1" value={draft.max_marks} onChange={(e) => setDraft({ ...draft, max_marks: e.target.value })} className="h-8 font-mono" data-testid={`tt-exam-max-${exam.id}`} />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={exam.status === "graded" ? "bg-[hsl(var(--success))/0.1] text-[hsl(var(--success))] border-[hsl(var(--success))/0.2]" : "text-muted-foreground"}>
            {exam.status}
          </Badge>
          {dirty && (
            <Button size="sm" variant="outline" className="h-7" onClick={save} data-testid={`tt-exam-save-${exam.id}`}>
              <Save className="h-3 w-3 mr-1" /> Save
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

function Empty({ msg }) {
  return <div className="p-10 text-center text-sm text-muted-foreground border border-border rounded-md bg-card">{msg}</div>;
}
function Loading() {
  return <div className="p-8 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline" /></div>;
}
function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider">{label}</Label>
      {children}
    </div>
  );
}
